import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { noop } from '@cian/newbuilding-utils';
import { Button, Tooltip } from '@cian/ui-kit';
import { IconToggleHeartOff24, IconToggleHeartOn24 } from '@cian/ui-kit-design-tokens/icons';
import { parseCookies } from '@cian/utils';
import { setCookie } from '@cian/utils/lib/browser/cookie';

import { trackingActionAddFromListingJG, trackingActionRemoveFromListingJK } from '../../actions/analytics';
import { updateFavorite } from '../../actions/favorites';
import { ClickOutside } from '../../components/outside';
import { MountPopup } from '../../components/PopupViewer';
import { IApplicationState } from '../../types/redux';
import { TTypedThunkDispatch } from '../../types/thunk';
import { MotivationPopup } from './MotivationPopup';

const ONE_DAY_IN_SECONDS = 24 * 60 * 60;
const TRUTHY_COOKIE_CONTENT = '1';
const DATA_MARK_NAME = 'FavoriteButton';

export const MOTIVATION_COOKIE_NAME = 'newbuilding-search-favorite_motivation';

interface IFavoriteButtonProps {
  isFavorite: boolean;
  newbuildingId: number;
  gaLabelFull: string;
  analyticsActionAddName?: string;
  analyticsActionRemoveName?: string;
}

export const FavoriteButton: React.FC<IFavoriteButtonProps> = ({
  isFavorite,
  newbuildingId,
  gaLabelFull,
  analyticsActionAddName,
  analyticsActionRemoveName,
}) => {
  const dispatch = useDispatch<TTypedThunkDispatch>();
  const userIsAuthenticated = useSelector((state: IApplicationState) => state.user.isAuthenticated);

  const [status, setStatus] = React.useState(isFavorite);
  const [showPopup, setShowPopup] = React.useState(false);

  const canShowMotivationPopup = React.useCallback(() => {
    const { __header_favorites_count__ } = window;
    const cookies = parseCookies(document.cookie);

    return !cookies[MOTIVATION_COOKIE_NAME] && !userIsAuthenticated && __header_favorites_count__ === 1;
  }, [userIsAuthenticated]);

  const closeMotivationPopup = React.useCallback(() => {
    setShowPopup(false);
  }, []);

  const showMotivationPopupIfNeeded = React.useCallback(() => {
    if (canShowMotivationPopup()) {
      setShowPopup(true);
      setCookie(MOTIVATION_COOKIE_NAME, TRUTHY_COOKIE_CONTENT, {
        expires: ONE_DAY_IN_SECONDS,
        secure: false,
      });
    }
  }, [canShowMotivationPopup]);

  const handleClick = React.useCallback(() => {
    const newStatus = !status;
    dispatch(
      updateFavorite({
        status: newStatus,
        newbuildingId,
      }),
    )
      .then(({ status }) => {
        setStatus(status);
        showMotivationPopupIfNeeded();
        if (status) {
          dispatch(trackingActionAddFromListingJG(gaLabelFull, analyticsActionAddName));
        } else {
          dispatch(trackingActionRemoveFromListingJK(gaLabelFull, analyticsActionRemoveName));
        }
      })
      .catch(noop);
  }, [
    status,
    dispatch,
    newbuildingId,
    showMotivationPopupIfNeeded,
    gaLabelFull,
    analyticsActionAddName,
    analyticsActionRemoveName,
  ]);

  return (
    <MountPopup
      placement="left"
      component={
        showPopup && (
          <ClickOutside onOutside={closeMotivationPopup}>
            <MotivationPopup onClose={closeMotivationPopup} />
          </ClickOutside>
        )
      }
    >
      <Tooltip placement="left" title={status ? 'В избранном' : 'Добавить в избранное'}>
        <Button
          beforeIcon={
            status ? (
              <IconToggleHeartOn24 color="icon-negative-default" />
            ) : (
              <IconToggleHeartOff24 color="icon-primary-default" />
            )
          }
          theme="stroke_secondary"
          size="M"
          onClick={handleClick}
          data-mark={DATA_MARK_NAME}
        />
      </Tooltip>
    </MountPopup>
  );
};
